// ABOUTME: OAuth callback handler for social login providers (Google, Facebook)
// ABOUTME: Completes authentication, stores tokens, and handles Alexa account linking if in progress
import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authAPI, completeAlexaOAuthFlow } from '../services/api';
import { Alert, Button, CircularProgress, Typography, Box, Paper } from '@mui/material';
import { decorativeGradients } from '../theme/tokens';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import LoginIcon from '@mui/icons-material/Login';

interface ErrorState {
  type: 'timeout' | 'validation' | 'oauth' | 'missing_data';
  message: string;
  details?: string;
}

const AuthCallback: React.FC = () => {
  const { setAuth } = useAuth();
  const [error, setError] = useState<ErrorState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [alexaLinkingSuccess, setAlexaLinkingSuccess] = useState(false);
  const hasExecuted = useRef(false);

  useEffect(() => {
    // EMERGENCY STOP - prevent multiple executions
    if (hasExecuted.current) {
      console.error('AuthCallback: Already executed, preventing duplicate execution');
      return;
    }
    hasExecuted.current = true;
    
    let timeoutId: NodeJS.Timeout;
    let redirected = false;
    
    const handleCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      const userId = urlParams.get('userId');
      const hasFamily = urlParams.get('hasFamily') === 'true';
      const error = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');
      const returnUrl = urlParams.get('returnUrl');

      // Log diagnostic information (no PII or tokens)
      const diagnosticInfo = {
        hasToken: !!token,
        hasUserId: !!userId,
        hasFamily: urlParams.get('hasFamily'),
        error,
        errorDescription,
        returnUrl,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      };

      console.error('AuthCallback diagnostic info:', diagnosticInfo);

      // Handle OAuth provider errors
      if (error) {
        setIsLoading(false);
        setError({
          type: 'oauth',
          message: 'OAuth Error',
          details: errorDescription ? decodeURIComponent(errorDescription) : error
        });
        return;
      }

      // Validate required parameters
      if (!token || !userId) {
        setIsLoading(false);
        setError({
          type: 'missing_data',
          message: 'Authentication Failed',
          details: 'Missing required data from authentication provider'
        });
        return;
      }

      // Validate token format (basic JWT check)
      if (!token.includes('.') || token.split('.').length !== 3) {
        setIsLoading(false);
        setError({
          type: 'validation',
          message: 'Authentication Failed',
          details: 'Invalid token format received'
        });
        return;
      }

      // Validate userId format
      const parsedUserId = parseInt(userId);
      if (isNaN(parsedUserId) || parsedUserId <= 0) {
        setIsLoading(false);
        setError({
          type: 'validation',
          message: 'Authentication Failed',
          details: 'Invalid user data received'
        });
        return;
      }

      // Fetch full user profile first to ensure complete user data including admin status
      let userProfile = null;
      try {
        const response = await fetch('/api/user/profile', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          userProfile = await response.json();
        }
      } catch (profileError) {
        console.error('Failed to fetch user profile:', profileError);
      }

      // Build auth response — refresh token is in HttpOnly cookie set by the server
      const authResponse = {
        token,
        userId: parsedUserId,
        email: userProfile?.email || '',
        firstName: userProfile?.firstName || '',
        lastName: userProfile?.lastName || '',
        profileImageUrl: userProfile?.profileImageUrl,
        isAdmin: userProfile?.isAdmin || false,
        hasFamily,
      };

      // Use authAPI.setAuth to properly store all authentication data including refresh tokens
      authAPI.setAuth(authResponse);

      // Build complete user object for auth context
      const completeUser = {
        id: parsedUserId,
        email: authResponse.email,
        firstName: authResponse.firstName,
        lastName: authResponse.lastName,
        profileImageUrl: authResponse.profileImageUrl,
        isAdmin: authResponse.isAdmin,
        hasFamily
      };

      // Update auth context with complete user data
      setAuth({
        isAuthenticated: true,
        user: completeUser,
        token
      });
      
      // Check if there's a pending Alexa linking to complete via social OAuth.
      // We use sessionStorage (written by SocialLoginButtons before OAuth redirect) rather than
      // reading alexa_linking/auth_code URL params that the backend also restores to the callback
      // URL — sessionStorage keeps the auth code out of browser history and server logs, which
      // is safer for a credential that can be exchanged for an Alexa token.
      const alexaAuthCode = sessionStorage.getItem('alexa_pending_auth_code');
      if (alexaAuthCode) {
        // Clear immediately — single use, prevent replay on back-navigation
        sessionStorage.removeItem('alexa_pending_auth_code');

        try {
          const alexaResult = await completeAlexaOAuthFlow({ authCode: alexaAuthCode });
          // Show success screen, then auto-redirect to Alexa after 2 seconds
          setIsLoading(false);
          setAlexaLinkingSuccess(true);
          redirected = true;
          window.sessionStorage.removeItem('auth_callback_running');
          setTimeout(() => {
            window.location.href = alexaResult.redirectUri;
          }, 2000);
        } catch {
          // Linking failed (e.g. auth code expired) — land on dashboard with error param
          redirected = true;
          window.sessionStorage.removeItem('auth_callback_running');
          window.location.href = '/?alexa_linking_error=true';
        }
        return;
      }

      // Standard redirect (no Alexa linking)
      try {
        const redirectUrl = returnUrl ? decodeURIComponent(returnUrl) : '/dashboard';
        redirected = true;

        // Clear loop prevention before redirect
        window.sessionStorage.removeItem('auth_callback_running');

        window.location.href = redirectUrl;
      } catch (redirectError) {
        console.error('Redirect failed:', redirectError);
        // If redirect fails, the timeout will handle it
      }
    };

    // Set up 5-second timeout
    timeoutId = setTimeout(() => {
      if (!redirected && isLoading) {
        console.error('AuthCallback timeout', {
          timeElapsed: 5000,
          parameters: { hasToken: !!(new URLSearchParams(window.location.search).get('token')) }
        });
        
        setIsLoading(false);
        setError({
          type: 'timeout',
          message: 'Sign In Timed Out',
          details: 'The authentication process took too long to complete. This might be due to a network issue.'
        });
      }
    }, 5000);

    handleCallback();

    return () => {
      clearTimeout(timeoutId);
      // Clean up loop prevention on unmount
      window.sessionStorage.removeItem('auth_callback_running');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isLoading intentionally omitted; adding it would cause the auth callback to re-run on every loading state change
  }, [setAuth]);

  const handleRetry = () => {
    setError(null);
    setIsLoading(true);
    window.location.reload();
  };

  const handleReturnToLogin = () => {
    window.location.href = '/login';
  };

  if (alexaLinkingSuccess) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: decorativeGradients.softBlue,
        }}
      >
        <Paper elevation={3} sx={{ p: 4, maxWidth: 448, width: '100%' }}>
          <Box display="flex" flexDirection="column" alignItems="center" textAlign="center" gap={2}>
            <Typography variant="h5" component="h2" gutterBottom>
              Your Alexa is connected!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Returning you to Alexa...
            </Typography>
            <CircularProgress size={32} sx={{ mt: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              You can close this window to return to Alexa.
            </Typography>
          </Box>
        </Paper>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: decorativeGradients.softBlue,
        }}
      >
        <Paper elevation={3} sx={{ p: 4, maxWidth: 448, width: '100%' }}>
          <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
            <ErrorOutlineIcon
              sx={{ fontSize: 48, color: 'error.main', mb: 2 }} 
            />
            
            <Alert severity="error" sx={{ width: '100%', mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {error.message}
              </Typography>
              {error.details && (
                <Typography variant="body2" color="text.secondary">
                  {error.details}
                </Typography>
              )}
            </Alert>

            <Box display="flex" gap={2} flexWrap="wrap" justifyContent="center">
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={handleRetry}
                color="primary"
              >
                Try Again
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<LoginIcon />}
                onClick={handleReturnToLogin}
                color="primary"
              >
                Return to Login
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: decorativeGradients.softBlue,
      }}
    >
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress
          size={48}
          sx={{ mb: 2 }}
        />
        <Typography variant="h5" component="h2" gutterBottom>
          Completing Sign In
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please wait while we finish setting up your account...
        </Typography>
      </Paper>
    </Box>
  );
};

export default AuthCallback;